import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Lock, LogOut, Mail, Users, Sigma, DollarSign, TrendingUp, Clock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ilmw_admin_token";

const StatCard = ({ icon: Icon, label, value, sub, color = "bg-white", testid }) => (
  <div className={`brutal-card p-5 ${color}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-heading text-xs uppercase tracking-widest font-bold opacity-70">{label}</span>
      <Icon strokeWidth={2.75} className="w-5 h-5" />
    </div>
    <div className="font-heading text-3xl font-bold mt-3 leading-none">{value}</div>
    {sub && <div className="text-xs font-semibold mt-1 opacity-70">{sub}</div>}
  </div>
);

const Section = ({ title, children, testid }) => (
  <section className="mt-8" data-testid={testid}>
    <h2 className="font-heading text-2xl font-bold mb-4">{title}</h2>
    <div className="brutal-card overflow-hidden">{children}</div>
  </section>
);

function LoginScreen({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pwd });
      localStorage.setItem(STORAGE_KEY, r.data.token);
      toast.success("Welcome back, admin");
      onLogin(r.data.token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} className="brutal-card p-8 w-full max-w-md" data-testid="admin-login-form">
        <Link to="/" className="text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100">← Back to site</Link>
        <div className="w-14 h-14 bg-brand-coral border-[3px] border-[#0A0A0A] rounded-xl flex items-center justify-center mt-4 mb-4">
          <Lock strokeWidth={3} className="w-7 h-7" />
        </div>
        <h1 className="font-heading text-3xl font-bold">Admin login</h1>
        <p className="text-sm text-muted-foreground mt-1">Enter the admin password to continue.</p>
        <label className="font-heading text-xs uppercase tracking-widest font-bold mt-6 mb-2 block">Password</label>
        <input
          type="password"
          autoFocus
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
          className="brutal-input px-4 py-3 w-full font-mono font-bold"
          data-testid="admin-password-input"
        />
        <button
          type="submit"
          disabled={loading || !pwd}
          className="brutal-btn bg-brand-blue text-white px-6 py-3 mt-5 w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 strokeWidth={3} className="w-5 h-5 animate-spin" /> : <Lock strokeWidth={3} className="w-5 h-5" />}
          {loading ? "Checking..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { "X-Admin-Token": token };

  useEffect(() => {
    const load = async () => {
      try {
        const [s, o, n] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/orders?limit=20`, { headers }),
          axios.get(`${API}/admin/newsletters?limit=50`, { headers }),
        ]);
        setStats(s.data);
        setOrders(o.data.orders);
        setSubs(n.data.subscribers);
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error("Session expired");
          onLogout();
        } else {
          toast.error("Failed to load admin data");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b-[3px] border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-yellow border-[3px] border-[#0A0A0A] rounded-xl flex items-center justify-center">
              <Lock strokeWidth={3} className="w-5 h-5 text-[#0A0A0A]" />
            </div>
            <div>
              <p className="font-heading text-xs uppercase tracking-widest font-bold opacity-70">Admin</p>
              <h1 className="font-heading text-xl font-bold">iLoveMathWorksheets</h1>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="brutal-btn bg-brand-coral text-[#0A0A0A] px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="admin-logout"
          >
            <LogOut strokeWidth={2.5} className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold">Overview</h2>
        <p className="text-muted-foreground text-sm mt-1">Live snapshot. Refresh the page to update.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6" data-testid="stats-grid">
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue_usd.toFixed(2)}`} sub={`${stats.paid_orders} paid orders`} color="bg-brand-green" testid="stat-revenue" />
          <StatCard icon={TrendingUp} label="Paid orders" value={stats.paid_orders} sub={`${stats.pending_orders} pending`} color="bg-brand-yellow" testid="stat-paid" />
          <StatCard icon={Mail} label="Newsletter" value={stats.newsletter_subscribers} sub="subscribers" color="bg-brand-coral" testid="stat-newsletter" />
          <StatCard icon={Users} label="Customers" value={stats.customers} sub="paid emails" color="bg-brand-blue text-white" testid="stat-customers" />
          <StatCard icon={Sigma} label="Worksheets" value={stats.worksheets_total} sub={`${stats.worksheets_free} free · ${stats.worksheets_paid} paid`} testid="stat-worksheets" />
          <StatCard icon={Clock} label="Avg revenue / order" value={stats.paid_orders > 0 ? `$${(stats.revenue_usd / stats.paid_orders).toFixed(2)}` : "—"} testid="stat-avg" />
          <div className="brutal-card p-5 col-span-2" data-testid="top-topics">
            <div className="flex items-center justify-between">
              <span className="font-heading text-xs uppercase tracking-widest font-bold opacity-70">Top paid topics</span>
            </div>
            {stats.topic_breakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground mt-3">No paid worksheets yet.</p>
            ) : (
              <ul className="mt-3 space-y-1.5 text-sm font-semibold">
                {stats.topic_breakdown.slice(0, 5).map((t) => (
                  <li key={t.topic} className="flex justify-between"><span className="capitalize">{t.topic}</span><span>{t.count}</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <Section title="Recent orders" testid="orders-section">
          {orders.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No orders yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white text-left">
                <tr>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">When</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Email</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Order ID</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Status</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t-2 border-[#0A0A0A]/10" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-semibold">{o.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{o.id}</td>
                    <td className="px-4 py-3">
                      <span className={`brutal-badge ${o.status === "COMPLETED" ? "bg-brand-green" : "bg-brand-yellow"}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-right">${o.amount_usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Newsletter subscribers" testid="subs-section">
          {subs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No subscribers yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white text-left">
                <tr>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Email</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Source</th>
                  <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t-2 border-[#0A0A0A]/10" data-testid={`sub-row-${s.id}`}>
                    <td className="px-4 py-3 font-semibold">{s.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.source}</td>
                    <td className="px-4 py-3 font-mono text-xs">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  const noindex = <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>;
  if (!token) return <>{noindex}<LoginScreen onLogin={setToken} /></>;
  return <>{noindex}<Dashboard token={token} onLogout={logout} /></>;
}
